import React from 'react';

import { Box, Paper, Typography } from '@material-ui/core';
import { TbFile, TbVideo, TbPhoto } from 'react-icons/tb';

import { bytesToSize } from '@vyce/core/src/utils';
import { FormAnswerMediaItem } from '@vyce/core/src/types';

const getExt = (value: string | undefined): string => {
  if (!value) return '';
  const withoutQuery = value.split('?')[0] || '';
  const parts = withoutQuery.split('.');
  if (parts.length < 2) return '';
  return (parts.pop() || '').toLowerCase();
};

const isImageLike = (item: FormAnswerMediaItem): boolean => {
  const mime = (item.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const ext = getExt(item.name) || getExt(item.url);
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif'].includes(ext);
};

const isVideoLike = (item: FormAnswerMediaItem): boolean => {
  const mime = (item.type || '').toLowerCase();
  if (mime.startsWith('video/')) return true;
  const ext = getExt(item.name) || getExt(item.url);
  return ['mp4', 'webm', 'mov', 'm4v', 'avi', 'mkv'].includes(ext);
};

export const FormResultMediaPreview: React.FC<{
  media: FormAnswerMediaItem[] | undefined | null;
  title?: string;
}> = ({ media, title }) => {
  const items = (media || []).filter(m => Boolean(m?.url));
  if (items.length === 0) return null;

  return (
    <Box display="flex" flexWrap="wrap" gridGap={12} width="100%" marginTop={2}>
      {items.map((item, idx) => {
        const key = item.url || `${item.name || 'file'}-${idx}`;
        const label = item.name || item.url;

        if (isImageLike(item)) {
          return (
            <a key={key} href={item.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex' }}>
              <img
                src={item.url}
                alt={item.name || title || 'image'}
                style={{
                  width: 140,
                  height: 140,
                  objectFit: 'cover',
                  borderRadius: 12,
                }}
              />
            </a>
          );
        }

        return (
          <a key={key} href={item.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <Paper
              variant="outlined"
              style={{
                padding: 12,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                gridGap: 12,
                maxWidth: 320,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center" width={28} height={28}>
                {isVideoLike(item) ? (
                  <TbVideo size={20} />
                ) : isImageLike(item) ? (
                  <TbPhoto size={20} />
                ) : (
                  <TbFile size={20} />
                )}
              </Box>
              <Box overflow="hidden" style={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 260,
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {typeof item.size === 'number' && item.size > 0
                    ? bytesToSize(item.size)
                    : item.type || 'File'}
                </Typography>
              </Box>
            </Paper>
          </a>
        );
      })}
    </Box>
  );
};
